import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';

export type FirstGenerationEntryPhase = 'plan' | 'style';

export interface FirstGenerationAcceptedContext {
  runId?: string;
  messageId?: string;
  baseVersionId?: string;
}

const STORAGE_KEY_PREFIX = 'wegic:first-generation-entry';
const REFERENCE_STORAGE_KEY_PREFIX = 'wegic:first-generation-reference';
const ACCEPTED_CONTEXT_STORAGE_KEY_PREFIX = 'wegic:first-generation-accepted-context';
const GENERATION_CONTINUATION_STORAGE_KEY_PREFIX = 'wegic:first-generation-continuation';

function getSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function getStorageKey(projectId: string): string {
  return `${STORAGE_KEY_PREFIX}:${projectId}`;
}

function getReferenceStorageKey(projectId: string): string {
  return `${REFERENCE_STORAGE_KEY_PREFIX}:${projectId}`;
}

function getAcceptedContextStorageKey(projectId: string): string {
  return `${ACCEPTED_CONTEXT_STORAGE_KEY_PREFIX}:${projectId}`;
}

function getGenerationContinuationStorageKey(projectId: string): string {
  return `${GENERATION_CONTINUATION_STORAGE_KEY_PREFIX}:${projectId}`;
}

export function markFirstGenerationContinuationPending(
  projectId: string,
  storage = getSessionStorage(),
): void {
  if (!projectId || !storage) {
    return;
  }

  try {
    storage.setItem(getGenerationContinuationStorageKey(projectId), 'pending');
  } catch {
    // Storage can be unavailable in private mode or embedded browsers.
  }
}

export function markFirstGenerationEntry(
  projectId: string,
  phase: FirstGenerationEntryPhase,
  reference?: ReferenceImage,
  storage = getSessionStorage(),
): void {
  markFirstGenerationEntryPhase(projectId, phase, storage);
  if (reference) {
    markFirstGenerationReference(projectId, reference, storage);
  }
}

export function markFirstGenerationContinuation(
  projectId: string,
  phase: FirstGenerationEntryPhase,
  reference?: ReferenceImage,
  storage = getSessionStorage(),
): void {
  markFirstGenerationContinuationPending(projectId, storage);
  markFirstGenerationEntry(projectId, phase, reference, storage);
}

export function clearFirstGenerationContinuationPending(
  projectId: string,
  storage = getSessionStorage(),
): void {
  if (!projectId || !storage) {
    return;
  }

  try {
    storage.removeItem(getGenerationContinuationStorageKey(projectId));
  } catch {
    // The code signal still takes priority for the current in-memory journey.
  }
}

export function hasPendingFirstGenerationContinuation(
  projectId: string,
  storage = getSessionStorage(),
): boolean {
  if (!projectId || !storage) {
    return false;
  }

  try {
    return storage.getItem(getGenerationContinuationStorageKey(projectId)) === 'pending';
  } catch {
    return false;
  }
}

export function markFirstGenerationEntryPhase(
  projectId: string,
  phase: FirstGenerationEntryPhase,
  storage = getSessionStorage(),
): void {
  if (!projectId || !storage) {
    return;
  }

  try {
    storage.setItem(getStorageKey(projectId), phase);
  } catch {
    // Storage can be unavailable in private mode or embedded browsers.
  }
}

export function consumeFirstGenerationEntryPhase(
  projectId: string,
  storage = getSessionStorage(),
): FirstGenerationEntryPhase | undefined {
  const phase = peekFirstGenerationEntryPhase(projectId, storage);
  if (!phase || !storage) {
    return phase;
  }

  try {
    storage.removeItem(getStorageKey(projectId));
  } catch {
    // The consumed value is still safe to use for the current navigation.
  }
  return phase;
}

export function peekFirstGenerationEntryPhase(
  projectId: string,
  storage = getSessionStorage(),
): FirstGenerationEntryPhase | undefined {
  if (!projectId || !storage) {
    return undefined;
  }

  let phase: string | null;
  try {
    phase = storage.getItem(getStorageKey(projectId));
  } catch {
    return undefined;
  }

  if (phase !== 'plan' && phase !== 'style') {
    return undefined;
  }
  return phase;
}

export function markFirstGenerationReference(
  projectId: string,
  reference: ReferenceImage,
  storage = getSessionStorage(),
): void {
  if (!projectId || !reference.url || !storage) {
    return;
  }

  try {
    storage.setItem(getReferenceStorageKey(projectId), JSON.stringify(reference));
  } catch {
    // Storage can be unavailable in private mode or embedded browsers.
  }
}

export function peekFirstGenerationReference(
  projectId: string,
  storage = getSessionStorage(),
): ReferenceImage | undefined {
  if (!projectId || !storage) {
    return undefined;
  }

  try {
    const stored = storage.getItem(getReferenceStorageKey(projectId));
    if (!stored) {
      return undefined;
    }

    const reference = JSON.parse(stored) as ReferenceImage;
    return reference?.url ? reference : undefined;
  } catch {
    return undefined;
  }
}

export function consumeFirstGenerationReference(
  projectId: string,
  storage = getSessionStorage(),
): ReferenceImage | undefined {
  const reference = peekFirstGenerationReference(projectId, storage);
  if (!reference || !storage) {
    return reference;
  }

  try {
    storage.removeItem(getReferenceStorageKey(projectId));
  } catch {
    // The consumed value is still safe to use for the current navigation.
  }
  return reference;
}

export function markFirstGenerationAcceptedContext(
  projectId: string,
  context: FirstGenerationAcceptedContext,
  storage = getSessionStorage(),
): void {
  if (!projectId || !storage) {
    return;
  }

  try {
    storage.setItem(getAcceptedContextStorageKey(projectId), JSON.stringify(context));
  } catch {
    // Storage can be unavailable in private mode or embedded browsers.
  }
}

export function peekFirstGenerationAcceptedContext(
  projectId: string,
  storage = getSessionStorage(),
): FirstGenerationAcceptedContext | undefined {
  if (!projectId || !storage) {
    return undefined;
  }

  try {
    const stored = storage.getItem(getAcceptedContextStorageKey(projectId));
    if (!stored) {
      return undefined;
    }

    const context = JSON.parse(stored) as FirstGenerationAcceptedContext;
    return context && typeof context === 'object' ? context : undefined;
  } catch {
    return undefined;
  }
}

export function consumeFirstGenerationAcceptedContext(
  projectId: string,
  storage = getSessionStorage(),
): FirstGenerationAcceptedContext | undefined {
  const context = peekFirstGenerationAcceptedContext(projectId, storage);
  if (!context || !storage) {
    return context;
  }

  try {
    storage.removeItem(getAcceptedContextStorageKey(projectId));
  } catch {
    // The consumed value is still safe to use for the current generation.
  }
  return context;
}

export function hasPendingFirstGenerationCodeSignalContext(
  projectId: string,
  storage = getSessionStorage(),
): boolean {
  if (!projectId) {
    return false;
  }

  const pendingEntryPhase = peekFirstGenerationEntryPhase(projectId, storage);
  const pendingReference = pendingEntryPhase
    ? peekFirstGenerationReference(projectId, storage)
    : undefined;
  if (pendingReference) {
    return true;
  }

  const acceptedContext = peekFirstGenerationAcceptedContext(projectId, storage);
  return Boolean(acceptedContext && !acceptedContext.baseVersionId);
}
